const DEFAULT_COUNTRY = "US";
const DEFAULT_LANGUAGE = "en";
const DEFAULT_TIMEZONE = "UTC";

const SUPPORTED_LANGUAGES = ["ja", "fr", "en", "es", "de", "ro"];

const languageCountryFallbacks = {
  de: "DE",
  en: "US",
  es: "ES",
  fr: "FR",
  ja: "JP",
  ro: "RO",
  zh: "CN",
};

const normalizeLanguageTag = (languageTag) => {
  if (!languageTag || typeof languageTag !== "string") {
    return "";
  }
  return languageTag.replace("_", "-").trim();
};

const getNavigatorLanguageTags = () => {
  if (typeof navigator === "undefined") {
    return [];
  }

  if (navigator.languages && navigator.languages.length > 0) {
    return navigator.languages;
  }

  return [navigator.language || navigator.userLanguage].filter(Boolean);
};

const getLocaleParts = (languageTag) => {
  const normalizedLanguageTag = normalizeLanguageTag(languageTag);
  if (!normalizedLanguageTag) {
    return {
      country: null,
      language: null,
    };
  }

  if (typeof Intl !== "undefined" && Intl.Locale) {
    try {
      const locale = new Intl.Locale(normalizedLanguageTag);
      const maximizedLocale = locale.maximize ? locale.maximize() : locale;
      return {
        country: (locale.region || maximizedLocale.region || null),
        language: locale.language || null,
      };
    } catch (e) {
      // Fall through to the simple parser below for older or unusual locale tags.
    }
  }

  const parts = normalizedLanguageTag.split("-");
  const language = parts[0] ? parts[0].toLowerCase() : null;
  const countryPart = parts.find((part, index) => index > 0 && /^[A-Za-z]{2}$/.test(part));

  return {
    country: countryPart || null,
    language,
  };
};

const normalizeCountry = (country) => {
  if (!country || typeof country !== "string") {
    return null;
  }

  const normalizedCountry = country.toUpperCase();
  return /^[A-Z]{2}$/.test(normalizedCountry) ? normalizedCountry : null;
};

const normalizeLanguage = (language) => {
  if (!language || typeof language !== "string") {
    return null;
  }

  const normalizedLanguage = language.toLowerCase();
  return /^[a-z]{2,3}$/.test(normalizedLanguage) ? normalizedLanguage : null;
};

const getBrowserLocaleDefaults = () => {
  const languageTags = getNavigatorLanguageTags();
  let language = null;
  let country = null;

  languageTags.some((languageTag) => {
    const localeParts = getLocaleParts(languageTag);
    language = language || normalizeLanguage(localeParts.language);
    country = country || normalizeCountry(localeParts.country);
    return Boolean(language && country);
  });

  country = country || languageCountryFallbacks[language] || DEFAULT_COUNTRY;

  return {
    country,
    language: language || DEFAULT_LANGUAGE,
  };
};

const getSupportedBrowserLocaleDefaults = () => {
  const defaults = getBrowserLocaleDefaults();
  return {
    ...defaults,
    language: SUPPORTED_LANGUAGES.includes(defaults.language) ? defaults.language : DEFAULT_LANGUAGE,
  };
};

const getBrowserTimezone = () => {
  if (typeof Intl === "undefined" || !Intl.DateTimeFormat) {
    return DEFAULT_TIMEZONE;
  }

  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return timezone || DEFAULT_TIMEZONE;
  } catch (e) {
    return DEFAULT_TIMEZONE;
  }
};

export {
  DEFAULT_COUNTRY,
  DEFAULT_LANGUAGE,
  DEFAULT_TIMEZONE,
  getBrowserLocaleDefaults,
  getBrowserTimezone,
  getSupportedBrowserLocaleDefaults,
};
